import { useState } from "react";
import axios from "axios";
import { Mail, Hash, Phone, Send, CheckCircle2, AlertCircle, Loader2, ChevronDown } from "lucide-react";
import Reveal from "./Reveal";

const SUBJECT_OPTIONS = [
  "Takunderhåll",
  "Fasadtvätt",
  "Bortforsling",
  "Övrigt",
];

function validateEmail(v) {
  if (!v) return true; // optional
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);
}

export default function Contact() {
  const [form, setForm] = useState({ subject: "", name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [serverError, setServerError] = useState("");
  const [submissionId, setSubmissionId] = useState(() => crypto.randomUUID());

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setSubmissionId(crypto.randomUUID());
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Namn krävs";
    if (!form.message.trim()) er.message = "Meddelande krävs";
    if (form.email && !validateEmail(form.email.trim())) er.email = "Ogiltig e-postadress";
    if (!form.email.trim() && !form.phone.trim()) {
      er.contact = "Ange minst e-post eller telefon";
    }
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;

    setStatus("loading");
    try {
      const res = await axios.post("https://forms.elido.nu/api/contact", {
        site_id: "svea-ytservice",
        submission_id: submissionId,
        website: "",
        subject: form.subject || null,
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      if (res.data && res.data.emailed) {
        setStatus("success");
        setForm({ subject: "", name: "", email: "", phone: "", message: "" });
        setSubmissionId(crypto.randomUUID());
      } else {
        setStatus("error");
        setServerError(
          "Något gick fel när meddelandet skulle skickas. Försök igen eller mejla oss direkt på info@sveaytservice.se."
        );
      }
    } catch (err) {
      setStatus("error");
      const detail = err?.response?.data?.detail;
      setServerError(
        typeof detail === "string"
          ? detail
          : "Något gick fel när meddelandet skulle skickas. Försök igen eller mejla oss direkt på info@sveaytservice.se."
      );
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="section-contact"
      className="bg-[#1A362B] text-[#F7F5F0] py-24 md:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 opacity-[0.05] bg-grain pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-12 lg:gap-20">
        {/* Left info */}
        <Reveal className="lg:col-span-5">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#F7F5F0]/65">
            Kontakt
          </div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-balance">
            Berätta vad du behöver{" "}
            <span className="text-[#C4A67B]">hjälp med</span>
          </h2>
          <p className="mt-5 text-[#F7F5F0]/75 leading-relaxed max-w-md">
            Skriv några rader om vad du vill ha hjälp med — tak, fasad eller
            något annat. Vi återkommer så snart vi kan.
          </p>

          <div className="mt-10 space-y-5">
            <a
              href="mailto:info@sveaytservice.se"
              data-testid="contact-email"
              className="flex items-start gap-4 group"
            >
              <div className="w-11 h-11 rounded-xl border border-[#F7F5F0]/15 flex items-center justify-center group-hover:bg-[#F7F5F0]/5 transition-colors">
                <Mail size={18} strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#F7F5F0]/55">
                  E-post
                </div>
                <div className="mt-1 text-[#F7F5F0] underline-offset-4 group-hover:underline">
                  info@sveaytservice.se
                </div>
              </div>
            </a>
            <a
              href="tel:+46738741447"
              data-testid="contact-phone"
              className="flex items-start gap-4 group"
            >
              <div className="w-11 h-11 rounded-xl border border-[#F7F5F0]/15 flex items-center justify-center group-hover:bg-[#F7F5F0]/5 transition-colors">
                <Phone size={18} strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#F7F5F0]/55">
                  Telefon
                </div>
                <div className="mt-1 text-[#F7F5F0] underline-offset-4 group-hover:underline">
                  073-874 14 47
                </div>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl border border-[#F7F5F0]/15 flex items-center justify-center">
                <Hash size={18} strokeWidth={1.6} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#F7F5F0]/55">
                  Org.nr
                </div>
                <div
                  data-testid="contact-orgnr"
                  className="mt-1 text-[#F7F5F0]"
                >
                  559586-3035
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Form */}
        <Reveal className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            noValidate
            className="bg-[#F7F5F0] text-[#1F2321] rounded-2xl p-7 md:p-10 shadow-[0_24px_60px_rgba(0,0,0,0.18)]"
          >
            {status === "success" ? (
              <div
                data-testid="contact-success"
                className="text-center py-10 px-2"
              >
                <div className="w-14 h-14 mx-auto rounded-full bg-[#1A362B] flex items-center justify-center">
                  <CheckCircle2 size={26} className="text-[#F7F5F0]" />
                </div>
                <h3 className="mt-6 font-serif-display text-2xl md:text-3xl">
                  Tack! Din förfrågan har skickats.
                </h3>
                <p className="mt-3 text-[#5C6B64] max-w-md mx-auto leading-relaxed">
                  Svea Ytservice återkommer så snart de kan.
                </p>
                <button
                  type="button"
                  data-testid="btn-send-again"
                  onClick={() => setStatus("idle")}
                  className="mt-8 inline-flex items-center bg-[#1A362B] text-[#F7F5F0] hover:bg-[#244739] rounded-full px-6 py-3 text-sm font-medium transition-colors"
                >
                  Skicka ett till
                </button>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-[#5C6B64] mb-2">
                    Vad behöver du hjälp med?
                  </label>
                  <div className="relative">
                    <select
                      data-testid="input-subject"
                      name="subject"
                      value={form.subject}
                      onChange={onChange}
                      className="svea-input w-full bg-white border border-[#E2DCD0] rounded-xl px-4 py-3 pr-10 text-[15px] appearance-none cursor-pointer"
                    >
                      <option value="">Välj ärende (valfritt)</option>
                      {SUBJECT_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={18}
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6B64]"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <Field
                    label="Namn"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    error={errors.name}
                    required
                    testid="input-name"
                    autoComplete="name"
                  />
                  <Field
                    label="Telefon"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={onChange}
                    error={errors.phone}
                    testid="input-phone"
                    autoComplete="tel"
                  />
                </div>
                <div className="mt-4">
                  <Field
                    label="E-post"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    error={errors.email}
                    testid="input-email"
                    autoComplete="email"
                  />
                </div>
                <div className="mt-4">
                  <label className="block text-xs uppercase tracking-[0.18em] text-[#5C6B64] mb-2">
                    Meddelande <span className="text-[#1A362B]">*</span>
                  </label>
                  <textarea
                    data-testid="input-message"
                    name="message"
                    rows={5}
                    value={form.message}
                    onChange={onChange}
                    className="svea-input w-full bg-white border border-[#E2DCD0] rounded-xl px-4 py-3 text-[15px] resize-y"
                    placeholder="Beskriv kort vad du vill ha hjälp med…"
                  />
                  {errors.message && (
                    <p className="mt-2 text-sm text-[#9b2c2c]">{errors.message}</p>
                  )}
                </div>

                {errors.contact && (
                  <p className="mt-4 text-sm text-[#9b2c2c]">{errors.contact}</p>
                )}

                {status === "error" && serverError && (
                  <div
                    data-testid="contact-error"
                    className="mt-5 flex items-start gap-3 bg-[#fdecea] border border-[#f5c2c0] text-[#9b2c2c] rounded-xl p-4 text-sm"
                  >
                    <AlertCircle size={18} className="mt-0.5 shrink-0" />
                    <span>{serverError}</span>
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="btn-submit-contact"
                  disabled={status === "loading"}
                  className="mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#1A362B] text-[#F7F5F0] hover:bg-[#244739] disabled:opacity-70 disabled:cursor-not-allowed rounded-full px-7 py-3.5 font-medium transition-colors"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Skickar…
                    </>
                  ) : (
                    <>
                      Skicka förfrågan
                      <Send size={16} />
                    </>
                  )}
                </button>
                <p className="mt-4 text-xs text-[#5C6B64]">
                  När du skickar formuläret behandlas dina uppgifter endast
                  för att kunna besvara din förfrågan.
                </p>
              </>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, error, required, testid, autoComplete }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-[#5C6B64] mb-2">
        {label} {required && <span className="text-[#1A362B]">*</span>}
      </label>
      <input
        data-testid={testid}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        className="svea-input w-full bg-white border border-[#E2DCD0] rounded-xl px-4 py-3 text-[15px]"
      />
      {error && <p className="mt-2 text-sm text-[#9b2c2c]">{error}</p>}
    </div>
  );
}
